import {
  ActivityIcon,
  ArrowLeftRightIcon,
  BarChart3Icon,
  BookOpenIcon,
  CalculatorIcon,
  CoinsIcon,
  WalletIcon,
  ArchiveIcon,
  DatabaseIcon,
  BrainCircuitIcon,
  BotIcon,
  BoxIcon,
  Building2Icon,
  CalendarDaysIcon,
  ClipboardCheckIcon,
  FileSignatureIcon,
  ClipboardListIcon,
  ClapperboardIcon,
  CloudIcon,
  FileIcon,
  FileTextIcon,
  GaugeIcon,
  MapPinnedIcon,
  Globe2Icon,
  HandshakeIcon,
  HouseIcon,
  LandmarkIcon,
  InboxIcon,
  LayoutDashboardIcon,
  LibraryIcon,
  LayersIcon,
  ListTreeIcon,
  ListTodoIcon,
  MailIcon,
  MailboxIcon,
  MegaphoneIcon,
  NetworkIcon,
  NewspaperIcon,
  PackageIcon,
  PhoneCallIcon,
  RadarIcon,
  ReceiptTextIcon,
  TrendingUpIcon,
  SettingsIcon,
  ShieldIcon,
  Share2Icon,
  UserRoundIcon,
  UsersIcon,
  WalletCardsIcon,
  type LucideIcon
} from 'lucide-react';
import { getStandardNavigationCatalogue, LIFECYCLE_CATEGORIES, LIFECYCLE_PLACEMENT, type LifecycleCategoryId, type NavigationStandardKey } from '@cdf/navigation-catalogue';

export { getStandardNavigationCatalogue } from '@cdf/navigation-catalogue';

/** A leaf link nested under a parent nav item (rendered as a sub heading). */
export type NavChild = {
  key: NavigationStandardKey;
  title: string;
  to: string;
};

/**
 * A sidebar entry: a direct link (`to`), an external link (`href`), or an
 * expandable group (`children`).
 */
export type NavItem = {
  key?: NavigationStandardKey;
  title: string;
  icon: LucideIcon;
  to?: string;
  href?: string;
  external?: boolean;
  children?: NavChild[];
  permissions?: string[];
  moduleKey?: string;
  dynamic?: 'public-microsite';
};

/** A titled section of the sidebar (rendered as a heading). */
export type NavSection = {
  heading: string;
  items: NavItem[];
  moduleKey?: string;
  lifecycleCategory?: LifecycleCategoryId;
};

const overview = (dashboardTitle = 'Dashboard'): NavSection => ({
  heading: 'Overview',
  moduleKey: 'overview',
  items: [
    { key: 'acquisition.agent', title: 'Agent', icon: BotIcon, to: '/agent', moduleKey: 'acquisition' },
    { key: 'overview.daily-briefing', title: 'Daily briefing', icon: NewspaperIcon, to: '/agent/briefing' },
    { key: 'overview.home', title: 'Overview', icon: HouseIcon, to: '/overview' },
    { key: 'overview.lifecycle', title: 'Lifecycle', icon: NetworkIcon, to: '/lifecycle' },
    { key: 'overview.next-actions', title: 'Relationship Health', icon: ActivityIcon, to: '/relationship-health', permissions: ['entity.records.read'] },
    { key: 'overview.sales-floor', title: 'Sales floor twin', icon: BoxIcon, to: '/sales-floor', permissions: ['entity.records.read'] },
    { key: 'overview.public-microsite', title: 'Public Micro Site', icon: Globe2Icon, to: '/sites/:slug', external: true, dynamic: 'public-microsite' },
    { key: 'overview.workflow-map', title: 'Workflow map', icon: NetworkIcon, to: '/workflow-map' },
    { key: 'overview.capability-map', title: 'Capability map', icon: LayersIcon, to: '/capability-map' },
    { key: 'overview.geography', title: 'Geographic map', icon: MapPinnedIcon, to: '/geography', permissions: ['entity.records.read'] },
    { key: 'overview.dashboard', title: dashboardTitle, icon: LayoutDashboardIcon, to: '/dashboard' },
    { key: 'files.workspace', title: 'Files', icon: FileIcon, to: '/files', permissions: ['entity.records.read'] }
  ]
});

export const personalProductivityNavigation: NavSection = {
  heading: 'Personal productivity',
  items: [
    { key: 'acquisition.outlook', title: 'Smart Outlook', icon: MailboxIcon, to: '/acquisition/outlook', moduleKey: 'acquisition' },
    { key: 'acquisition.shared-mailbox', title: 'Shared mailbox', icon: UsersIcon, to: '/acquisition/shared-mailbox', moduleKey: 'acquisition' },
    { key: 'crm.icloud', title: 'My iCloud contacts', icon: CloudIcon, to: '/partner-engagement/icloud', moduleKey: 'crm' }
  ]
};

const acquisitionItems: NavItem[] = [
  { key: 'acquisition.whatsapp', title: 'WhatsApp', icon: InboxIcon, to: '/whatsapp', permissions: ['entity.records.read'] },
  { key: 'acquisition.social', title: 'Social workspace', icon: Share2Icon, to: '/acquisition/social' },
  { key: 'acquisition.email', title: 'Email workspace', icon: MailIcon, to: '/acquisition/email' },
  { key: 'acquisition.mailbox-monitoring', title: 'Mailbox monitoring', icon: InboxIcon, to: '/acquisition/mailbox-monitoring', permissions: ['entity.records.read'] },
  { key: 'acquisition.voice', title: 'AI phone agent', icon: PhoneCallIcon, to: '/acquisition/voice' },
  { key: 'acquisition.web-signals', title: 'Web signals', icon: RadarIcon, to: '/acquisition/web-signals' },
  { key: 'acquisition.intelligence', title: 'Market intelligence', icon: TrendingUpIcon, to: '/acquisition/intelligence' },
  { key: 'acquisition.analytics', title: 'Acquisition analytics', icon: BarChart3Icon, to: '/acquisition/analytics' }
];

export const contentHubNavigation: NavSection = {
  heading: 'Content Hub',
  moduleKey: 'content-hub',
  items: [
    { key: 'content-hub.dashboard', title: 'Dashboard', icon: LayoutDashboardIcon, to: '/content-hub' },
    { key: 'content-hub.generate', title: 'Generate', icon: ActivityIcon, to: '/content-hub/generate', permissions: ['entity.content.manage'] },
    { key: 'content-hub.posts', title: 'Post library', icon: FileTextIcon, to: '/content-hub/posts', permissions: ['entity.content.manage'] },
    { key: 'content-hub.media', title: 'Media library', icon: LibraryIcon, to: '/content-hub/media', permissions: ['entity.content.manage'] },
    { key: 'content-hub.knowledge', title: 'Knowledge', icon: BookOpenIcon, to: '/content-hub/knowledge', permissions: ['entity.content.manage'] },
    { key: 'content-hub.video', title: 'Video Studio', icon: ClapperboardIcon, to: '/content-hub/video', permissions: ['entity.content.manage'] },
    { key: 'content-hub.calendar', title: 'Calendar', icon: CalendarDaysIcon, to: '/content-hub/calendar', permissions: ['entity.content.manage'] },
    { key: 'content-hub.intelligence', title: 'Local intelligence', icon: BrainCircuitIcon, to: '/content-hub/intelligence', permissions: ['entity.content.manage'] },
    { key: 'content-hub.ads', title: 'Advertising', icon: MegaphoneIcon, to: '/content-hub/ads', permissions: ['entity.content.manage'] },
    { key: 'content-hub.leads', title: 'Leads', icon: UsersIcon, to: '/content-hub/leads' },
    { key: 'content-hub.bookings', title: 'Bookings', icon: CalendarDaysIcon, to: '/content-hub/bookings' },
    { key: 'content-hub.sequences', title: 'Sequences', icon: MailIcon, to: '/content-hub/sequences' },
    { key: 'content-hub.lead-magnet', title: 'Lead magnet', icon: LibraryIcon, to: '/content-hub/lead-magnet' },
    { key: 'content-hub.mailing-list', title: 'Mailing list', icon: UsersIcon, to: '/content-hub/mailing-list' },
    { key: 'content-hub.authority', title: 'Authority builder', icon: ShieldIcon, to: '/content-hub/authority' },
    { key: 'content-hub.operations', title: 'Operations', icon: SettingsIcon, to: '/content-hub/operations' },
    { key: 'content-hub.entity-profile', title: 'Entity Profile', icon: Building2Icon, to: '/content-hub/entity-profile', permissions: ['entity.profile.manage'] },
    { key: 'content-hub.microsite', title: 'Microsite', icon: Globe2Icon, to: '/manage/microsite', permissions: ['entity.content.manage'] },
    { key: 'content-hub.review-requests', title: 'Review requests', icon: ClipboardListIcon, to: '/content-hub/review-requests' },
    { key: 'content-hub.reports', title: 'Reports', icon: BarChart3Icon, to: '/content-hub/reports' },
    { key: 'content-hub.automation', title: 'Automation', icon: BotIcon, to: '/content-hub/automation' },
    { key: 'content-hub.go-live', title: 'Go-live checklist', icon: ListTodoIcon, to: '/content-hub/go-live' },
    { key: 'content-hub.how-it-works', title: 'How it works', icon: BookOpenIcon, to: '/content-hub/how-it-works' }
  ]
};

export const researchNavigation: NavSection = {
  heading: 'Research',
  moduleKey: 'research',
  items: [
    { key: 'research.workspace', title: 'Research', icon: BookOpenIcon, to: '/research', permissions: ['entity.records.read'] }
  ]
};

const interactionsNavigation = (items = acquisitionItems, enquiriesTitle = 'Enquiries'): NavSection => ({
  heading: 'Interactions',
  items: [
    { key: 'crm.insights', title: 'Lifecycle insights & improvements', icon: BarChart3Icon, to: '/insights/lifecycle', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.retention', title: 'Retention & archive', icon: HandshakeIcon, to: '/partner-engagement/retention', permissions: ['entity.manage', 'entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.data-quality', title: 'Data quality & ownership', icon: HandshakeIcon, to: '/partner-engagement/data-quality', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.renewals', title: 'Renewal & growth planning', icon: HandshakeIcon, to: '/partner-engagement/renewals', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.reviews', title: 'Account reviews & feedback', icon: HandshakeIcon, to: '/partner-engagement/reviews', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.cases', title: 'Cases, requests & complaints', icon: HandshakeIcon, to: '/partner-engagement/cases', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.delivery', title: 'Delivery plans & handovers', icon: HandshakeIcon, to: '/partner-engagement/delivery', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.forecast', title: 'Opportunity forecast', icon: BarChart3Icon, to: '/partner-engagement/forecast', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.agreements', title: 'Agreements & obligations', icon: HandshakeIcon, to: '/partner-engagement/agreements', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.discovery', title: 'Discovery & negotiation', icon: NetworkIcon, to: '/partner-engagement/discovery', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.activities', title: 'Interactions & tasks', icon: NetworkIcon, to: '/connect/activities', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.stakeholders', title: 'Stakeholders', icon: NetworkIcon, to: '/partner-engagement/stakeholders', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.referrals', title: 'Referrals', icon: HandshakeIcon, to: '/connect/referrals', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.opportunity-hub', title: 'Opportunity Hub', icon: InboxIcon, to: '/partner-engagement/opportunity-hub', permissions: ['entity.records.read'], moduleKey: 'crm' },
    { key: 'crm.enquiries', title: enquiriesTitle, icon: InboxIcon, to: '/connect/enquiries', moduleKey: 'crm' },
    { key: 'crm.social-leads', title: 'Social Leads', icon: MegaphoneIcon, to: '/connect/social-leads', moduleKey: 'crm' },
    ...items.map((item) => ({ ...item, moduleKey: 'acquisition' }))
  ]
});

const originalNavigationByMode = {
  all: [overview('All data dashboard'), personalProductivityNavigation, {
    heading: 'Relationships', items: [
      { key: 'crm.accounts', title: 'Accounts', icon: Building2Icon, to: '/partner-engagement/accounts', moduleKey: 'crm' },
      { key: 'crm.opportunities', title: 'Opportunities', icon: HandshakeIcon, to: '/partner-engagement/opportunities', moduleKey: 'crm' },
      { key: 'crm.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'], moduleKey: 'crm' },
      { key: 'crm.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'], moduleKey: 'crm' },
      { key: 'crm.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'], moduleKey: 'crm' },
      { key: 'crm.contacts', title: 'Contacts', icon: UsersIcon, to: '/partner-engagement/contacts', moduleKey: 'crm' },
      { key: 'partner.overview', title: 'Partner overview', icon: GaugeIcon, to: '/partner-engagement/overview', moduleKey: 'partner-engagement' },
      { key: 'partner.partners', title: 'Partners', icon: NetworkIcon, to: '/partner-engagement/partners', moduleKey: 'partner-engagement' }
    ]
  }, interactionsNavigation(), contentHubNavigation, researchNavigation],
  b2b: [overview('B2B dashboard'), personalProductivityNavigation, {
    moduleKey: 'crm',
    heading: 'Business CRM', items: [
      { key: 'crm.accounts', title: 'Accounts', icon: Building2Icon, to: '/partner-engagement/accounts' },
      { key: 'crm.opportunities', title: 'Opportunities', icon: HandshakeIcon, to: '/partner-engagement/opportunities' },
      { key: 'crm.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'] },
      { key: 'crm.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'] },
      { key: 'crm.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'] },
      { key: 'crm.contacts', title: 'Contacts', icon: UsersIcon, to: '/partner-engagement/contacts' },
    ]
  }, interactionsNavigation(), contentHubNavigation, researchNavigation],
  b2c: [overview('B2C dashboard'), personalProductivityNavigation, {
    moduleKey: 'crm',
    heading: 'Consumer CRM', items: [
      { key: 'crm.accounts', title: 'Consumers', icon: UserRoundIcon, to: '/partner-engagement/accounts' },
      { key: 'crm.opportunities', title: 'Consumer funnel', icon: HandshakeIcon, to: '/partner-engagement/opportunities' },
      { key: 'crm.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'] },
      { key: 'crm.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'] },
      { key: 'crm.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'] },
      { key: 'crm.contacts', title: 'Interactions', icon: UsersIcon, to: '/partner-engagement/contacts' },
    ]
  }, interactionsNavigation(acquisitionItems.filter((item) => ['WhatsApp', 'Email workspace', 'Agent', 'AI phone agent', 'Acquisition analytics'].includes(item.title)), 'Consumer enquiries'), contentHubNavigation, researchNavigation],
  partner: [overview('Partner dashboard'), personalProductivityNavigation, {
    heading: 'Partner Engagement', items: [
      { key: 'partner.overview', title: 'Partner overview', icon: GaugeIcon, to: '/partner-engagement/overview', moduleKey: 'partner-engagement' },
      { key: 'partner.partners', title: 'Partners', icon: NetworkIcon, to: '/partner-engagement/partners', moduleKey: 'partner-engagement' },
      { key: 'crm.contacts', title: 'Contacts', icon: UsersIcon, to: '/partner-engagement/contacts', moduleKey: 'crm' },
      { key: 'crm.accounts', title: 'All accounts', icon: Building2Icon, to: '/partner-engagement/accounts', moduleKey: 'crm' },
      { key: 'crm.opportunities', title: 'Joint opportunities', icon: HandshakeIcon, to: '/partner-engagement/opportunities', moduleKey: 'crm' },
      { key: 'crm.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'], moduleKey: 'crm' },
      { key: 'crm.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'], moduleKey: 'crm' },
      { key: 'crm.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'], moduleKey: 'crm' }
    ]
  }, interactionsNavigation(), contentHubNavigation, researchNavigation],
  investor: [overview('Investor dashboard'), personalProductivityNavigation, {
    moduleKey: 'crm',
    heading: 'Investor relations', items: [
      { key: 'crm.accounts', title: 'Investor directory', icon: WalletCardsIcon, to: '/partner-engagement/accounts' },
      { key: 'crm.opportunities', title: 'Funding rounds', icon: HandshakeIcon, to: '/partner-engagement/opportunities' },
      { key: 'crm.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'] },
      { key: 'crm.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'] },
      { key: 'crm.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'] },
      { key: 'crm.contacts', title: 'Interaction history', icon: UsersIcon, to: '/partner-engagement/contacts' },
    ]
  }, interactionsNavigation(acquisitionItems.filter((item) => ['WhatsApp', 'Email workspace', 'Agent', 'Market intelligence', 'Acquisition analytics'].includes(item.title)), 'Investor enquiries'), contentHubNavigation, researchNavigation]
} satisfies Record<'all' | 'b2b' | 'b2c' | 'partner' | 'investor', NavSection[]>;

function regroupStandardNavigation(sections: readonly NavSection[]): NavSection[] {
  const catalogue = new Map(getStandardNavigationCatalogue().map(item => [item.key, item]));
  const items = sections.flatMap(section => section.items.map(item => ({
    ...item,
    moduleKey: item.moduleKey ?? section.moduleKey,
    permissions: item.permissions ?? (item.key ? catalogue.get(item.key)?.permissions?.slice() : undefined)
  })));
  return [
    { heading: 'Home', items: items.filter(item => item.key && (item.key === 'acquisition.agent' || LIFECYCLE_PLACEMENT[item.key] === 'utilities')) },
    ...LIFECYCLE_CATEGORIES.map(category => ({
      heading: category.title,
      lifecycleCategory: category.id,
      items: items.filter(item => item.key && item.key !== 'acquisition.agent' && LIFECYCLE_PLACEMENT[item.key] === category.id)
    }))
  ];
}

export const navigationByMode = {
  all: regroupStandardNavigation(originalNavigationByMode.all),
  b2b: regroupStandardNavigation(originalNavigationByMode.b2b),
  b2c: regroupStandardNavigation(originalNavigationByMode.b2c),
  partner: regroupStandardNavigation(originalNavigationByMode.partner),
  investor: regroupStandardNavigation(originalNavigationByMode.investor)
};

/**
 * Standard definitions remain authoritative for breadcrumbs, overview cards
 * and other route discovery. Only the application sidebar applies an entity's
 * saved presentation layout through resolveNavigation().
 */
export const mainNavigation = navigationByMode.b2b;
export function filterNavigationByModules(sections: readonly NavSection[], enabledModules?: readonly string[]): NavSection[] {
  if (!enabledModules) return sections.map((section) => ({ ...section, items: [...section.items] }));
  const enabled = new Set(enabledModules);
  return sections.flatMap((section) => {
    if (section.moduleKey && !enabled.has(section.moduleKey)) return [];
    const items = section.items.filter((item) => !item.moduleKey || enabled.has(item.moduleKey));
    return items.length || section.lifecycleCategory ? [{ ...section, items }] : [];
  });
}
export const getMainNavigation = (mode: keyof typeof navigationByMode, enabledModules?: readonly string[]) =>
  filterNavigationByModules(navigationByMode[mode], enabledModules);

/**
 * Administration navigation — rendered from the active permission snapshot.
 */
export const entityManagementNavigation: NavSection = {
  heading: 'Entity admin',
  items: ([
    { key: 'admin.entity', title: 'Entity administration', icon: Building2Icon, to: '/admin/entity', permissions: ['entity.manage', 'entity.records.write', 'entity.content.manage', 'entity.profile.manage', 'entity.social-connections.manage', 'entity.integrations.manage'] },
    { key: 'admin.microsite', title: 'Microsite', icon: Globe2Icon, to: '/manage/microsite', permissions: ['entity.content.manage'] },
    { key: 'admin.catalogue', title: 'Catalogue', icon: ListTreeIcon, to: '/manage/catalogue' },
    { key: 'admin.services', title: 'Service catalogue', icon: PackageIcon, to: '/manage/services' },
    { key: 'admin.account-intelligence', title: 'Account intelligence', icon: BrainCircuitIcon, to: '/manage/account-intelligence' },
    { key: 'admin.quotes', title: 'Quotes', icon: FileTextIcon, to: '/manage/quotes', permissions: ['entity.records.write'] },
    { key: 'admin.tenders', title: 'Proposals', icon: ClipboardCheckIcon, to: '/manage/proposals', permissions: ['entity.records.read'] },
    { key: 'admin.sows', title: 'Statements of Work', icon: FileSignatureIcon, to: '/manage/sows', permissions: ['entity.records.read'] },
    { key: 'admin.business-cases', title: 'Business cases', icon: ClipboardListIcon, to: '/manage/business-cases' },
    { key: 'admin.content', title: 'Content catalogue', icon: LibraryIcon, to: '/manage/content' },
    { key: 'admin.events', title: 'Events', icon: CalendarDaysIcon, to: '/manage/events' },
    { key: 'admin.custom-pages', title: 'Custom pages', icon: LayoutDashboardIcon, to: '/manage/pages' },
    { key: 'admin.homepage', title: 'Homepage configuration', icon: LayoutDashboardIcon, to: '/settings/homepage' },
    { key: 'admin.legacy-data', title: 'Legacy data', icon: ArchiveIcon, to: '/manage/legacy-data' },
    { key: 'admin.legacy-import', title: 'Legacy import', icon: DatabaseIcon, to: '/manage/legacy-import' },
  ] satisfies NavItem[]).map((item) => ({ ...item, permissions: item.permissions ?? ['tenant.members.manage'] }))
};

export const entityAdminNavigation: NavSection = {
  heading: 'Entity admin', items: entityManagementNavigation.items.filter(item => item.key === 'admin.entity')
};

export const tenantAdminNavigation: NavSection = {
  heading: 'Tenant admin', items: [
    { key: 'tenant.admin', title: 'Tenant administration', icon: Building2Icon, to: '/admin/tenant', permissions: ['tenant.members.manage', 'tenant.roles.assign', 'tenant.roles.manage', 'tenant.groups.manage', 'tenant.settings.manage', 'tenant.tokens.read', 'tenant.tokens.manage', 'tenant.data.manage', 'tenant.audit.read', 'entity.social-connections.manage'] },
  ]
};
export const platformAdminNavigation: NavSection = {
  heading: 'Platform admin', items: [
    { key: 'platform.admin', title: 'Platform administration', icon: ShieldIcon, to: '/admin/platform' },
  ]
};
export function getAdminNavigation(hasPermission: (key: string) => boolean, platformPermissions: readonly string[]): NavSection[] {
  const has = (key: string) => key.startsWith('platform.') ? platformPermissions.includes(key) : hasPermission(key);
  return [
    ...(has('entity.manage') || has('entity.records.write') || has('entity.content.manage') || has('entity.profile.manage') || has('entity.social-connections.manage') || has('entity.integrations.manage') ? [entityAdminNavigation] : []),
    ...(['tenant.members.manage', 'tenant.roles.assign', 'tenant.roles.manage', 'tenant.groups.manage', 'tenant.settings.manage', 'tenant.tokens.read', 'tenant.tokens.manage', 'tenant.data.manage', 'tenant.audit.read', 'entity.social-connections.manage'].some(has) ? [tenantAdminNavigation] : []),
    ...(platformPermissions.some((key) => key.startsWith('platform.')) ? [platformAdminNavigation] : [])
  ].map((section) => ({ ...section, items: section.items.filter((item) => !item.permissions || item.permissions.some(has)) }));
}

/**
 * Xero sidebar entries — rendered only when the Xero integration is enabled
 * in Settings → Accounting (the single on/off switch for the Xero section).
 */
export const xeroNavItems: NavItem[] = [
  { key: 'accounting.xero', title: 'Xero (mock)', icon: ReceiptTextIcon, to: '/manage/xero' },
  { key: 'accounting.xero-bank-feed', title: 'Xero bank feed', icon: LandmarkIcon, to: '/manage/xero/bank-transactions' },
  { key: 'accounting.xero-reconciliation', title: 'Xero reconciliation', icon: ArrowLeftRightIcon, to: '/manage/xero/reconciliation' }
];

/** Non-Xero accounting platforms that get a sidebar entry once connected. */
export type AccountingPlatformKey = 'myob' | 'quickbooks' | 'reckon' | 'saasu';

export const accountingPlatformNavItems: Record<AccountingPlatformKey, NavItem> = {
  myob: { key: 'accounting.myob', title: 'MYOB', icon: CalculatorIcon, to: '/manage/accounting/myob' },
  quickbooks: {
    key: 'accounting.quickbooks',
    title: 'QuickBooks Online',
    icon: BookOpenIcon,
    to: '/manage/accounting/quickbooks'
  },
  reckon: { key: 'accounting.reckon', title: 'Reckon One', icon: WalletIcon, to: '/manage/accounting/reckon' },
  saasu: { key: 'accounting.saasu', title: 'Saasu', icon: CoinsIcon, to: '/manage/accounting/saasu' }
};

/**
 * Accounting sidebar section — Xero only for now. Other platforms stay in
 * Settings → Accounting until their CRM workflows ship.
 */
export function buildAccountingNavigation(): NavSection {
  return { heading: 'Accounting', items: xeroNavItems };
}

export type Breadcrumb = {
  section: string;
  item: string;
  child?: string;
};

/** Resolve the breadcrumb trail for a pathname from the nav config. */
export function findBreadcrumb(
  pathname: string,
  mode: keyof typeof navigationByMode = 'all'
): Breadcrumb | undefined {
  // Breadcrumbs must resolve even for accounting entries that are currently
  // hidden from the sidebar, so resolve against the fully enabled sections.
  const breadcrumbSections = [
    entityAdminNavigation, tenantAdminNavigation, platformAdminNavigation,
    buildAccountingNavigation(),
    {
      heading: 'Accounting',
      items: Object.values(accountingPlatformNavItems)
    },
    contentHubNavigation
  ];
  for (const section of [...navigationByMode[mode], ...breadcrumbSections]) {
    for (const item of section.items) {
      if (item.to === pathname) {
        return { section: section.heading, item: item.title };
      }
      const child = item.children?.find((entry) => entry.to === pathname);
      if (child) {
        return { section: section.heading, item: item.title, child: child.title };
      }
      // Direct links with nested routes (e.g. /settings/users) still resolve
      // to their parent entry.
      if (item.to && item.to !== '/' && pathname.startsWith(`${item.to}/`)) {
        return { section: section.heading, item: item.title };
      }
    }
  }
  return undefined;
}

/**
 * Page icon registry — the single source of truth for icons used in both
 * the nav (sidebar/top-nav) and the PageHeader banner on each page.
 *
 * Rule: never import a page header icon directly from 'lucide-react'.
 * Always import it from here so nav and page stay in sync automatically.
 */
export {
  ActivityIcon,
  BarChart3Icon as AcquisitionAnalyticsIcon,
  ArchiveIcon,
  DatabaseIcon,
  BrainCircuitIcon,
  BotIcon as AgentIcon,
  BoxIcon as SalesFloorTwinIcon,
  Building2Icon,
  CalendarDaysIcon,
  ClipboardListIcon,
  FileIcon,
  FileTextIcon,
  GaugeIcon,
  Globe2Icon as MicrositeIcon,
  HandshakeIcon,
  HouseIcon as HomeIcon,
  InboxIcon,
  LayoutDashboardIcon,
  LibraryIcon,
  LayersIcon,
  ListTreeIcon,
  ListTodoIcon as NextActionsIcon,
  MailIcon as EmailIcon,
  MailboxIcon as SmartOutlookIcon,
  MegaphoneIcon as SocialLeadsIcon,
  NetworkIcon,
  NetworkIcon as WorkflowMapIcon,
  MapPinnedIcon as GeographicMapIcon,
  PackageIcon,
  PhoneCallIcon as VoiceIcon,
  RadarIcon as WebSignalsIcon,
  ReceiptTextIcon as XeroIcon,
  LandmarkIcon as BankFeedIcon,
  ArrowLeftRightIcon as ReconcileIcon,
  CalculatorIcon as MyobIcon,
  BookOpenIcon as QuickBooksIcon,
  WalletIcon as ReckonIcon,
  CoinsIcon as SaasuIcon,
  TrendingUpIcon as MarketIntelligenceIcon,
  SettingsIcon,
  ShieldIcon,
  Share2Icon as SocialIcon,
  UsersIcon
};
